import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Menu, X } from 'lucide-react';
import { useState } from 'react';
import RouteMeta from '@/components/RouteMeta';

const Layout = ({ children }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'About', href: '/about' },
    { name: 'Forms', href: '/forms' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-blue-50 to-white">
      <RouteMeta />
      <a href="#main-content" className="skip-link">Skip to main content</a>
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top Bar */}
          <div className="py-2 border-b border-blue-500/30 text-sm">
            <div className="flex flex-wrap justify-between items-center gap-2">
              <div className="flex items-center gap-4 flex-wrap">
                <a href="tel:3049275262" className="flex items-center gap-1 hover:text-blue-200 transition-colors">
                  <Phone className="h-4 w-4" />
                  <span>(304) 927-5262</span>
                </a>
                <a href="mailto:psych_srvcs@yahoo.com" aria-label="Email Psych Services at psych_srvcs@yahoo.com" className="flex items-center gap-1 hover:text-blue-200 transition-colors">
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  <span className="hidden sm:inline">psych_srvcs@yahoo.com</span>
                </a>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                <span>315 Market St, Spencer, WV 25276</span>
              </div>
            </div>
          </div>

          {/* Main Navigation */}
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex flex-col">
              <h1 className="text-2xl font-bold">Psych Services</h1>
              <p className="text-sm text-blue-200">of Roane County, Inc.</p>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-4 py-2 rounded-md font-medium transition-colors ${
                    isActive(item.href)
                      ? 'bg-blue-700 text-white'
                      : 'text-blue-100 hover:bg-blue-700/50 hover:text-white'
                  }`}
                  aria-current={isActive(item.href) ? 'page' : undefined}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-md hover:bg-blue-700/50 transition-colors"
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav id="mobile-navigation" aria-label="Mobile navigation" className="md:hidden pb-4 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-2 rounded-md font-medium transition-colors ${
                    isActive(item.href)
                      ? 'bg-blue-700 text-white'
                      : 'text-blue-100 hover:bg-blue-700/50 hover:text-white'
                  }`}
                  data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                  aria-current={isActive(item.href) ? 'page' : undefined}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main id="main-content" className="flex-grow" tabIndex="-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-slate-800 to-slate-900 text-white mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div>
              <h2 className="text-lg font-semibold mb-4 text-blue-300">Contact Us</h2>
              <div className="space-y-3 text-slate-300">
                <div className="flex items-start gap-2">
                  <MapPin className="h-5 w-5 mt-0.5 flex-shrink-0" />
                  <span>315 Market St<br />Spencer, WV 25276-1811</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  <a href="tel:3049275262" className="hover:text-blue-300 transition-colors">(304) 927-5262</a>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  <a href="mailto:psych_srvcs@yahoo.com" className="hover:text-blue-300 transition-colors">psych_srvcs@yahoo.com</a>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h2 className="text-lg font-semibold mb-4 text-blue-300">Quick Links</h2>
              <div className="space-y-2">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="block text-slate-300 hover:text-blue-300 transition-colors"
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Social & Crisis */}
            <div>
              <h2 className="text-lg font-semibold mb-4 text-blue-300">Connect With Us</h2>
              <a
                href="https://www.facebook.com/profile.php?id=100064047403908"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-md transition-colors mb-4"
                data-testid="facebook-link"
              >
                <Facebook className="h-5 w-5" />
                Follow Us on Facebook
              </a>
              <div className="mt-4 p-4 bg-red-900/50 border border-red-700 rounded-md">
                <p className="font-semibold text-red-200 mb-1">Crisis Support 24/7</p>
                <p className="text-sm text-slate-300">National Suicide Prevention:</p>
                <a href="tel:988" className="text-lg font-bold text-red-200 hover:text-red-100">988</a>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-slate-700 text-center text-slate-400 text-sm">
            <p className="mb-2">© {new Date().getFullYear()} Psych Services of Roane County, Inc. All rights reserved.</p>
            <p className="text-xs">315 Market St, Spencer, WV 25276-1811 | (304) 927-5262</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
